import { useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '@arcanecircle/ui/grimoire/button'
import { IconButton } from '@arcanecircle/ui/grimoire/icon-button'
import { socketService } from '@/services/socket'
import {
  fetchCharacterVTTData,
  getCachedCharacter,
  formatModifier,
  getAbilityName,
  getCharacterSheetUrl,
  type VTTCharacterData,
} from '@/services/characterService'
import { useVTTStore } from '@/store'
import { useInitiativeStore } from '@/store/initiativeStore'
import { isDiceRollMessage } from '@/types'
import { useDragOffset } from '@/hooks/useDragOffset'

interface CharacterSheetModalProps {
  characterId: string
  /** VTT JWT — forwarded to the platform character fetch. */
  authToken: string
  onClose: () => void
}

/** Preferred display order for the six d20 ability scores. Unknown
 *  ability keys (homebrew systems) are appended after these. */
const ABILITY_ORDER = ['str', 'dex', 'con', 'int', 'wis', 'cha']

/** Common dice for the quick-roll tray — works for every system. */
const DICE_TRAY = [4, 6, 8, 10, 12, 20, 100]

/** Build standard dice notation for a d20 + modifier roll. */
function d20Notation(mod: number): string {
  if (!mod) return '1d20'
  return mod > 0 ? `1d20+${mod}` : `1d20-${Math.abs(mod)}`
}

/** Build a Burning-Wheel-style dice-pool notation:
 *   - `N` is the stat value driving the pool size.
 *   - `bonus` adds arbitrary extra dice on top.
 *   - `explode` triggers a follow-up d6 on every 6 (and recursively) —
 *     the BWG "open-ended sixes" granted by spending Fate Artha. The
 *     sheet defaults this OFF and only sets it true when the player
 *     ticks the Fate Artha box in the pre-roll confirm modal.
 *   - `threshold` is the shade target (Black=4, Gray=3, White=2). BW
 *     only ever counts successes against the obstacle — there is no
 *     raw-sum mode — so the threshold is always emitted and clamped to
 *     `[1, 6]` at the input field.
 *
 * Clamps the dice count to a minimum of 1 — `0d6` errors in the parser
 * and a stat with rating 0 still needs *something* to roll.
 */
function bwNotation(
  N: number,
  opts: { bonus: number; explode: boolean; threshold: number },
): string {
  const total = Math.max(1, Math.floor(N) + Math.floor(opts.bonus))
  const bang = opts.explode ? '!' : ''
  const safeThreshold = Math.max(1, Math.min(6, Math.floor(opts.threshold)))
  return `${total}d6${bang}>=${safeThreshold}`
}

/** Turn a camelCase / snake_case stat key into a display label
 *  ("animalHandling" → "Animal Handling", "sleight_of_hand" → "Sleight Of Hand"). */
function humanizeKey(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function orderedAbilityKeys(abilities: Record<string, unknown>): string[] {
  const keys = Object.keys(abilities)
  const known = ABILITY_ORDER.filter((k) => k in abilities)
  const extra = keys.filter((k) => !ABILITY_ORDER.includes(k))
  return [...known, ...extra]
}

/**
 * Foundry-style character sheet modal.
 *
 * Mounted once at the app root and driven by `store.characterSheetId`.
 * Loads the full vtt-data for the character (cache-first) and renders
 * the complete sheet — HP / AC / Speed / Init, the six ability scores,
 * saving throws, and skills for d20 systems; a flat custom-stat list
 * for everything else. Every stat gets a one-click roll button that
 * emits a `dice_roll` to chat (just like typing `/roll 1d20+5`), so the
 * GM and players can roll straight from the sheet without leaving it.
 */
type RollMode = 'd20' | 'bw'

/** Parse a Burning-Wheel-style stat value into a dice-pool size. BW
 *  ratings ship from the platform as concatenated strings like "B4" or
 *  "G5" (shade letter + numeric rating). Tolerates bare numbers ("4")
 *  too in case the formatter drops the shade. Returns null when the
 *  value isn't BW-shaped — caller falls back to a no-roll display. */
function parseBwRating(value: string | number | undefined): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.floor(value)
  }
  if (typeof value !== 'string') return null
  const m = value.trim().match(/^([A-Za-z]?)(\d+)$/)
  if (!m) return null
  return parseInt(m[2], 10)
}

export function CharacterSheetModal({
  characterId,
  authToken,
  onClose,
}: CharacterSheetModalProps) {
  const [character, setCharacter] = useState<VTTCharacterData | null>(
    () => getCachedCharacter(characterId),
  )
  const [loading, setLoading] = useState(!character)
  const [error, setError] = useState<string | null>(null)
  // Drag the sheet by its header (offsets from the centred position).
  const drag = useDragOffset()

  // Roll mode is derived from the character's systemKind — Burning Wheel
  // characters always roll dice pools, d20 characters always roll
  // 1d20+mod. No manual toggle: the platform knows what system the
  // sheet belongs to.
  const rollMode: RollMode =
    character?.systemKind === 'burning-wheel' ? 'bw' : 'd20'

  // BW knobs (only meaningful when rollMode === 'bw'). Reset per-mount
  // to BWG defaults: 4+ counts as success, no bonus dice. Exploding 6s
  // ("open-ended sixes" in BWG) costs Fate Artha and is opted-in per
  // roll via the pre-roll confirm modal — never a sticky toggle.
  const [bwThreshold, setBwThreshold] = useState<number>(4)
  const [bwBonusDice, setBwBonusDice] = useState<number>(0)

  // Pending BW roll waiting on the Fate Artha confirm modal. `null` when
  // no roll is in flight; otherwise carries the pool size and label so
  // the modal can show what the user is about to spend Artha on.
  const [pendingBwRoll, setPendingBwRoll] = useState<
    { pool: number; label: string } | null
  >(null)

  // Track when the sheet opened so the footer only echoes rolls made
  // from this point on (vs. surfacing stale history).
  const openedAt = useRef(new Date().toISOString())
  const messages = useVTTStore((s) => s.messages)
  const lastRoll = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]
      if (isDiceRollMessage(m) && m.timestamp >= openedAt.current) return m
    }
    return null
  }, [messages])

  // Escape closes the modal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  // Always refetch on open. The useState initializer seeded the modal
  // with cached data for instant feedback; this effect fires a
  // background fetch with `bypassCache: true` so the GM sees any edits
  // made on the platform without a full reload. characterId is stable
  // per mount (App.tsx toggles us null between sheets), so no resync
  // branch.
  useEffect(() => {
    let cancelled = false
    const hadCache = getCachedCharacter(characterId) !== null
    if (!hadCache) {
      setLoading(true)
      setError(null)
    }
    fetchCharacterVTTData(characterId, authToken, { bypassCache: true })
      .then((data) => {
        if (cancelled) return
        if (data) setCharacter(data)
        else if (!hadCache) setError('Character data is unavailable.')
      })
      .catch(() => {
        if (!cancelled && !hadCache) setError('Failed to load character.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [characterId, authToken])

  const sendRoll = (notation: string, label?: string) => {
    try {
      socketService.rollDice(notation, label)
    } catch (err) {
      console.error('[CharacterSheetModal] roll failed:', err)
    }
  }

  // ── Initiative roll-in (issue #162) ────────────────────────────────────────
  // The Init value is a clickable button. Clicking it rolls this character into
  // an already-started initiative. "Started" is gated on the GM having created
  // the initiative row: the store's `mapId` equals the active map only once a
  // row exists. We resolve the character's token on the active map to key the
  // entry; if there is none (character not placed) the button can't add.
  const getActiveMap = useVTTStore((s) => s.getActiveMap)
  const getActiveMapTokens = useVTTStore((s) => s.getActiveMapTokens)
  const initiativeMapId = useInitiativeStore((s) => s.mapId)

  const activeMap = getActiveMap()
  // Token for this character on the active map (the entry's stable key).
  const myToken = activeMap
    ? getActiveMapTokens().find((t) => t.characterId === characterId) ?? null
    : null
  // The GM-gated start signal: a non-empty store mapId matching the active map
  // means the GM has started initiative for it. No match ⇒ not started.
  const initiativeStarted = !!activeMap && initiativeMapId === activeMap.id
  const canRollInitiative = initiativeStarted && !!myToken

  /** Roll d20 + the sheet's initiative modifier and emit it into the active
   *  (already-started) initiative. No-op when not started or unplaced — the
   *  server also rejects a roll with no row, so a stray click can never start
   *  combat. */
  const rollInitiativeInto = () => {
    if (!activeMap || !myToken || !character) return
    if (!initiativeStarted) {
      // Defensive: the button is disabled in this state, but never silently
      // start combat from a player click.
      useInitiativeStore
        .getState()
        .setError('Initiative has not been started by the GM.')
      return
    }
    const mod = character.stats.initiative
    // Roll ONCE and use the same die for both the initiative entry and the
    // chat line. Previously chat was rolled separately (sendRoll → a fresh
    // server-side dice_roll), so the list and chat showed two different
    // numbers — Arbiter repro: rolled 3, the list showed 2. #162.
    const die = Math.floor(Math.random() * 20) + 1
    const total = die + mod
    const rollerName = myToken.name || character.name
    try {
      socketService.emitInitiativeRoll(activeMap.id, {
        tokenId: myToken.id,
        name: rollerName,
        value: total,
      })
      // Post the SAME roll to chat, labeled — not a second re-roll.
      const modStr =
        mod === 0 ? '' : mod > 0 ? ` + ${mod}` : ` − ${Math.abs(mod)}`
      socketService.sendChatMessage(
        `${rollerName} rolled Initiative: d20 (${die})${modStr} = ${total}`,
      )
    } catch (err) {
      console.error('[CharacterSheetModal] initiative roll failed:', err)
    }
  }

  /** Stage a Burning Wheel roll. We never send the dice straight to chat
   *  — first the Fate Artha modal asks whether to make 6s open-ended.
   *  The d20 path stays direct because d20 has no per-roll Artha gate. */
  const stageBwRoll = (pool: number, label: string) => {
    setPendingBwRoll({ pool, label })
  }
  const cancelBwRoll = () => setPendingBwRoll(null)
  const confirmBwRoll = (spendFateArtha: boolean) => {
    if (!pendingBwRoll) return
    const notation = bwNotation(pendingBwRoll.pool, {
      bonus: bwBonusDice,
      explode: spendFateArtha,
      threshold: bwThreshold,
    })
    sendRoll(notation, pendingBwRoll.label || undefined)
    setPendingBwRoll(null)
  }

  /** Preview notation for a stat — for tooltips / labels only. Always
   *  shows the no-Artha shape, since exploding is decided at roll time. */
  const rollStat = (
    d20Mod: number,
    bwPool: number,
    title: string,
  ): { notation: string; title: string } => {
    if (rollMode === 'bw') {
      const notation = bwNotation(bwPool, {
        bonus: bwBonusDice,
        explode: false,
        threshold: bwThreshold,
      })
      return { notation, title: `${title} — ${notation}` }
    }
    const notation = d20Notation(d20Mod)
    return { notation, title: `${title} (${notation})` }
  }
  /** Dispatch a stat roll. d20 fires immediately; BW pops the Artha
   *  confirm modal first. */
  const sendStatRoll = (d20Mod: number, bwPool: number, label = '') => {
    if (rollMode === 'bw') {
      stageBwRoll(bwPool, label)
      return
    }
    const { notation } = rollStat(d20Mod, bwPool, '')
    sendRoll(notation, label || undefined)
  }

  const systemKind = character?.systemKind ?? 'd20'
  const isD20 = systemKind === 'd20'

  const hpMax = Math.max(1, character?.stats.hp.max ?? 1)
  const hpCur = character?.stats.hp.current ?? 0
  const hpTemp = character?.stats.hp.temp ?? 0
  const hpPct = Math.max(0, Math.min(100, (hpCur / hpMax) * 100))
  const tempPct = Math.min(100 - hpPct, (hpTemp / hpMax) * 100)

  const portrait = character?.imageUrl ?? null
  const initial = (character?.name || '?').charAt(0).toUpperCase()

  const metaLine = character
    ? [
        humanizeKey(character.system || ''),
        character.level != null ? `Level ${character.level}` : null,
        character.playerName,
      ]
        .filter(Boolean)
        .join(' · ')
    : ''

  return (
    <div className="vtt-modal-backdrop" onClick={onClose}>
      <div
        className="vtt-modal-surface"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 600,
          transform: `translate(${drag.offset.x}px, ${drag.offset.y}px)`,
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Character sheet"
      >
        {/* Header — portrait + identity + close. Doubles as the drag handle. */}
        <div
          className="vtt-modal-header"
          onPointerDown={drag.onPointerDown}
          style={{
            cursor: drag.dragging ? 'grabbing' : 'grab',
            touchAction: 'none',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
            {portrait ? (
              <img
                src={portrait}
                alt=""
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  flexShrink: 0,
                  border: '1px solid hsl(var(--ac-dark-accent))',
                }}
              />
            ) : (
              <span
                aria-hidden
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'hsl(var(--ac-dark-primary))',
                  border: '1px solid hsl(var(--ac-dark-accent))',
                  color: 'hsl(var(--ac-text-muted))',
                  fontFamily: 'var(--ac-font-serif)',
                  fontSize: 18,
                  flexShrink: 0,
                }}
              >
                {initial}
              </span>
            )}
            <div style={{ minWidth: 0 }}>
              <div className="ac-panel-eyebrow">Character sheet</div>
              <div
                className="ac-panel-title"
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {character?.name ?? 'Loading…'}
              </div>
              {metaLine && (
                <div
                  className="ac-help"
                  style={{ marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {metaLine}
                </div>
              )}
            </div>
          </div>
          <IconButton
            size="sm"
            variant="ghost"
            aria-label="Close"
            onClick={onClose}
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </IconButton>
        </div>

        <div className="vtt-modal-body">
          {loading ? (
            <div className="ac-help" style={{ textAlign: 'center', padding: '1.5rem' }}>
              Loading character…
            </div>
          ) : error || !character ? (
            <div
              className="ac-help"
              style={{ textAlign: 'center', padding: '1.5rem', color: 'hsl(var(--ac-danger))' }}
            >
              {error ?? 'Character data is unavailable.'}
            </div>
          ) : (
            <>
              {/* Burning Wheel dice-pool knobs — only shown when the
                  character's systemKind is 'burning-wheel'. Mode itself
                  is derived from the character data, not user-toggled. */}
              {rollMode === 'bw' && (
                <BwKnobsPanel
                  bwThreshold={bwThreshold}
                  setBwThreshold={setBwThreshold}
                  bwBonusDice={bwBonusDice}
                  setBwBonusDice={setBwBonusDice}
                />
              )}
              {isD20 ? (
                <>
                  {/* HP bar */}
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        justifyContent: 'space-between',
                        fontSize: '0.6875rem',
                        marginBottom: 4,
                      }}
                    >
                      <span
                        style={{
                          color: 'hsl(var(--ac-text-muted))',
                          letterSpacing: '0.12em',
                          textTransform: 'uppercase',
                        }}
                      >
                        Hit points
                      </span>
                      <span className="font-mono-ac">
                        <b style={{ color: 'hsl(var(--ac-text-light))', fontSize: '0.875rem' }}>
                          {hpCur}
                        </b>
                        {hpTemp > 0 && (
                          <span style={{ color: 'hsl(var(--ac-info))' }}> +{hpTemp}</span>
                        )}
                        <span style={{ color: 'hsl(var(--ac-text-muted))' }}>
                          {' '}/ {character.stats.hp.max}
                        </span>
                      </span>
                    </div>
                    <div className="vtt-hp-bar" style={{ height: 10 }}>
                      <div className="fill" style={{ width: `${hpPct}%` }} />
                      {hpTemp > 0 && (
                        <div className="temp" style={{ left: `${hpPct}%`, width: `${tempPct}%` }} />
                      )}
                    </div>
                  </div>

                  {/* AC / Speed / Init / Proficiency */}
                  <div
                    className="vtt-stat-grid"
                    style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}
                  >
                    <div className="vtt-stat">
                      <div className="k">AC</div>
                      <div className="v">{character.stats.ac}</div>
                    </div>
                    <div className="vtt-stat">
                      <div className="k">Speed</div>
                      <div className="v">
                        {character.stats.speed}
                        <small>ft</small>
                      </div>
                    </div>
                    {/* Init is a button — clicking rolls this character into an
                        already-started initiative (issue #162). Styled to read
                        as a button (border + pointer + mint "Roll" affordance),
                        matching the ability-score roll tiles below so players
                        recognize it as clickable (issue #161 follow-up #3a).
                        Gated on the GM having started it and the character being
                        placed on the active map; disabled otherwise so a click
                        never silently starts combat. */}
                    <button
                      type="button"
                      className="vtt-stat"
                      onClick={rollInitiativeInto}
                      disabled={!canRollInitiative}
                      title={
                        !myToken
                          ? 'Place this character on the active map to roll initiative'
                          : !initiativeStarted
                            ? 'The GM has not started initiative yet'
                            : `Roll into initiative (${d20Notation(
                                character.stats.initiative,
                              )})`
                      }
                      style={{
                        position: 'relative',
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                        // Match the AC/Speed/Prof sibling cells' footprint; the
                        // border + pointer + mint die are what signal "button".
                        border: canRollInitiative
                          ? '1px solid hsl(var(--ac-mint-primary) / 0.5)'
                          : '1px solid hsl(var(--ac-dark-accent))',
                        borderRadius: 6,
                        background: canRollInitiative
                          ? 'hsl(var(--ac-mint-primary) / 0.08)'
                          : 'none',
                        font: 'inherit',
                        color: 'inherit',
                        cursor: canRollInitiative ? 'pointer' : 'not-allowed',
                        opacity: canRollInitiative ? 1 : 0.55,
                      }}
                    >
                      <div className="k" style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                        Init
                        <DieIcon size={9} />
                      </div>
                      <div className="v">
                        {formatModifier(character.stats.initiative)}
                      </div>
                    </button>
                    <div className="vtt-stat">
                      <div className="k">Prof</div>
                      <div className="v">{formatModifier(character.stats.proficiencyBonus)}</div>
                    </div>
                  </div>

                  {/* Ability scores — each rolls a d20 + mod ability check */}
                  <Section title="Ability scores">
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: '0.5rem',
                      }}
                    >
                      {orderedAbilityKeys(character.abilities).map((key) => {
                        const ability = character.abilities[key]
                        const { title: rollTitle } = rollStat(
                          ability.mod,
                          ability.score,
                          `Roll ${getAbilityName(key)} check`,
                        )
                        return (
                          <button
                            key={key}
                            type="button"
                            className="vtt-stat"
                            title={rollTitle}
                            onClick={() =>
                              sendStatRoll(
                                ability.mod,
                                ability.score,
                                getAbilityName(key),
                              )
                            }
                            style={{
                              textAlign: 'center',
                              cursor: 'pointer',
                              border: '1px solid hsl(var(--ac-dark-accent))',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 2,
                              padding: '0.5rem 0.25rem',
                            }}
                          >
                            <div className="k">{key.toUpperCase()}</div>
                            <div className="v" style={{ fontSize: '1.25rem' }}>
                              {formatModifier(ability.mod)}
                            </div>
                            <div style={{ fontSize: '0.625rem', color: 'hsl(var(--ac-text-muted))' }}>
                              {ability.score}
                            </div>
                            <span
                              aria-hidden
                              style={{
                                marginTop: 2,
                                fontSize: '0.5625rem',
                                letterSpacing: '0.1em',
                                textTransform: 'uppercase',
                                color: 'hsl(var(--ac-mint-primary))',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 3,
                              }}
                            >
                              <DieIcon size={10} /> Roll
                            </span>
                          </button>
                        )
                      })}
                    </div>
                  </Section>

                  {/* Saving throws */}
                  {Object.keys(character.saves).length > 0 && (
                    <Section title="Saving throws">
                      <StatRollList
                        entries={orderedAbilityKeys(character.saves).map((key) => ({
                          key,
                          label: getAbilityName(key),
                          rollLabel: `${getAbilityName(key)} Save`,
                          mod: character.saves[key].mod,
                          proficient: character.saves[key].proficient,
                        }))}
                        onRoll={(mod, label) => sendStatRoll(mod, mod, label)}
                        rollMode={rollMode}
                      />
                    </Section>
                  )}

                  {/* Skills */}
                  {Object.keys(character.skills).length > 0 && (
                    <Section title="Skills">
                      <StatRollList
                        entries={Object.entries(character.skills)
                          .map(([key, s]) => ({
                            key,
                            label: humanizeKey(key),
                            mod: s.mod,
                            proficient: s.proficient,
                          }))
                          .sort((a, b) => a.label.localeCompare(b.label))}
                        onRoll={(mod, label) => sendStatRoll(mod, mod, label)}
                        rollMode={rollMode}
                        twoColumn
                      />
                    </Section>
                  )}
                </>
              ) : (
                /* Non-d20 systems — flat custom-stat list. For Burning
                   Wheel, each stat renders as a clickable card whose roll
                   parses N out of values like "B4"/"G5". For 'generic',
                   the cards are inert displays. */
                <Section title="Attributes">
                  {character.customStats && character.customStats.length > 0 ? (
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '0.5rem',
                      }}
                    >
                      {character.customStats.map((stat) => {
                        const bwPool =
                          rollMode === 'bw' ? parseBwRating(stat.value) : null
                        const rollable = bwPool != null && bwPool > 0
                        if (!rollable) {
                          return (
                            <div key={stat.key} className="vtt-stat">
                              <div className="k">{stat.label}</div>
                              <div className="v" style={{ fontSize: '1rem' }}>
                                {stat.value}
                              </div>
                            </div>
                          )
                        }
                        // Preview notation (no Artha) for the tooltip only —
                        // actual notation is rebuilt at confirm time based on
                        // the player's Fate Artha choice.
                        const notation = bwNotation(bwPool, {
                          bonus: bwBonusDice,
                          explode: false,
                          threshold: bwThreshold,
                        })
                        return (
                          <button
                            key={stat.key}
                            type="button"
                            className="vtt-stat"
                            title={`Roll ${stat.label} — ${notation}`}
                            onClick={() => stageBwRoll(bwPool, stat.label)}
                            style={{
                              textAlign: 'center',
                              cursor: 'pointer',
                              border: '1px solid hsl(var(--ac-dark-accent))',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 2,
                              padding: '0.5rem 0.25rem',
                            }}
                          >
                            <div className="k">{stat.label}</div>
                            <div className="v" style={{ fontSize: '1.25rem' }}>
                              {stat.value}
                            </div>
                            <span
                              aria-hidden
                              style={{
                                marginTop: 2,
                                fontSize: '0.5625rem',
                                letterSpacing: '0.1em',
                                textTransform: 'uppercase',
                                color: 'hsl(var(--ac-mint-primary))',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 3,
                              }}
                            >
                              <DieIcon size={10} /> {bwPool}d6
                            </span>
                          </button>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="ac-help">
                      This system doesn't expose structured stats to the VTT. Use the dice
                      tray below or the chat composer to roll.
                    </div>
                  )}
                </Section>
              )}

              {/* Dice tray — quick single-die rolls for every system. */}
              <Section title="Dice tray">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
                  {DICE_TRAY.map((sides) => (
                    <Button
                      key={sides}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => sendRoll(`1d${sides}`, `d${sides}`)}
                      title={`Roll 1d${sides}`}
                      className="font-mono-ac gap-1"
                    >
                      <DieIcon size={12} />d{sides}
                    </Button>
                  ))}
                </div>
              </Section>
            </>
          )}
        </div>

        {/* Footer — last roll echo + actions */}
        <div className="vtt-modal-footer" style={{ justifyContent: 'space-between' }}>
          <div className="ac-help" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {lastRoll ? (
              <span className="font-mono-ac">
                Rolled <span style={{ color: 'hsl(var(--ac-text-light))' }}>{lastRoll.notation}</span>
                {' → '}
                <span style={{ color: 'hsl(var(--ac-mint-primary))', fontWeight: 600 }}>{lastRoll.total}</span>
              </span>
            ) : (
              <span>Click a stat to roll it in chat.</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
            {character && (
              <Button asChild variant="outline" size="sm">
                <a
                  href={getCharacterSheetUrl(characterId)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Full sheet ↗
                </a>
              </Button>
            )}
            <Button type="button" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>

      {pendingBwRoll && (
        <BwFateArthaModal
          pool={pendingBwRoll.pool}
          bonusDice={bwBonusDice}
          threshold={bwThreshold}
          label={pendingBwRoll.label}
          onCancel={cancelBwRoll}
          onConfirm={confirmBwRoll}
        />
      )}
    </div>
  )
}

/**
 * Pre-roll confirm for Burning Wheel: pick whether to spend Fate Artha
 * to make 6s open-ended. Defaults unchecked — Fate is a finite resource
 * and shouldn't be the default spend. Roll button always submits the
 * dice, with or without explosion. Esc / overlay click cancels.
 */
function BwFateArthaModal({
  pool,
  bonusDice,
  threshold,
  label,
  onCancel,
  onConfirm,
}: {
  pool: number
  bonusDice: number
  threshold: number
  label: string
  onCancel: () => void
  onConfirm: (spendFateArtha: boolean) => void
}) {
  const [artha, setArtha] = useState(false)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
      else if (e.key === 'Enter') onConfirm(artha)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onCancel, onConfirm, artha])

  const preview = bwNotation(pool, { bonus: bonusDice, explode: artha, threshold })

  return (
    <div
      className="vtt-modal-backdrop"
      onClick={onCancel}
      style={{ zIndex: 1000 }}
    >
      <div
        className="vtt-modal-surface"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 360 }}
        role="dialog"
        aria-modal="true"
        aria-label="Burning Wheel roll — Fate Artha"
      >
        <div className="vtt-modal-header" style={{ paddingBottom: '0.5rem' }}>
          <div>
            <div className="ac-panel-eyebrow" style={{ borderTop: 'none', paddingTop: 0 }}>
              Burning Wheel roll
            </div>
            <div className="ac-panel-title">{label || 'Pool roll'}</div>
          </div>
        </div>
        <div className="vtt-modal-body" style={{ gap: '0.75rem' }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.5rem',
              cursor: 'pointer',
              fontSize: '0.8125rem',
              color: 'hsl(var(--ac-text-light))',
            }}
          >
            <input
              type="checkbox"
              checked={artha}
              onChange={(e) => setArtha(e.target.checked)}
              style={{ marginTop: 2 }}
            />
            <span>
              <b>Spend Fate Artha</b>
              <div className="ac-help" style={{ marginTop: 2 }}>
                6s explode (open-ended sixes). Costs 1 Fate.
              </div>
            </span>
          </label>
          <div
            className="ac-help font-mono-ac"
            style={{ textAlign: 'center', fontSize: '0.8125rem' }}
          >
            {preview}
          </div>
        </div>
        <div
          className="vtt-modal-footer"
          style={{ justifyContent: 'flex-end', gap: '0.5rem' }}
        >
          <Button type="button" variant="outline" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" size="sm" onClick={() => onConfirm(artha)}>
            Roll
          </Button>
        </div>
      </div>
    </div>
  )
}

/** Burning Wheel dice-pool knobs. Rendered only when the character's
 *  systemKind is 'burning-wheel'. The exploding-6s control lives in the
 *  per-roll Fate Artha modal — it's a spend, not a sticky preference,
 *  so it doesn't belong here. */
function BwKnobsPanel({
  bwThreshold,
  setBwThreshold,
  bwBonusDice,
  setBwBonusDice,
}: {
  bwThreshold: number
  setBwThreshold: (n: number) => void
  bwBonusDice: number
  setBwBonusDice: (n: number) => void
}) {
  return (
    <div
      className="ac-card-flat"
      style={{
        padding: '0.5rem 0.625rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
      }}
    >
      <span
        className="ac-panel-eyebrow"
        style={{ borderTop: 'none', paddingTop: 0 }}
      >
        Burning Wheel pool
      </span>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '0.5rem',
          alignItems: 'end',
        }}
      >
        <NumberField
          label="Success ≥"
          value={bwThreshold}
          min={1}
          max={6}
          onChange={setBwThreshold}
          hint="shade: B=4, G=3, W=2"
        />
        <NumberField
          label="Bonus dice"
          value={bwBonusDice}
          min={0}
          max={20}
          onChange={setBwBonusDice}
        />
      </div>
    </div>
  )
}

function NumberField({
  label,
  value,
  min,
  max,
  onChange,
  hint,
}: {
  label: string
  value: number
  min: number
  max: number
  onChange: (n: number) => void
  hint?: string
}) {
  return (
    <div>
      <label
        className="ac-help"
        style={{
          fontSize: '0.6875rem',
          display: 'block',
          marginBottom: 4,
        }}
      >
        {label}
        {hint && (
          <span
            style={{
              marginLeft: 6,
              color: 'hsl(var(--ac-text-muted))',
              fontSize: '0.625rem',
            }}
          >
            ({hint})
          </span>
        )}
      </label>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => {
          const raw = parseInt(e.target.value, 10)
          if (Number.isNaN(raw)) return
          onChange(Math.max(min, Math.min(max, raw)))
        }}
        className="ac-card-flat"
        style={{
          width: '100%',
          padding: '0.375rem 0.5rem',
          fontSize: '0.875rem',
          color: 'hsl(var(--ac-text-light))',
          background: 'transparent',
          border: '1px solid hsl(var(--ac-dark-accent))',
          borderRadius: 'var(--ac-radius)',
          textAlign: 'center',
        }}
      />
    </div>
  )
}

/** Labelled section wrapper with a small eyebrow header. */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div
        className="ac-panel-eyebrow"
        style={{ marginBottom: '0.5rem', paddingTop: '0.25rem', borderTop: '1px solid hsl(var(--ac-dark-accent))' }}
      >
        {title}
      </div>
      {children}
    </div>
  )
}

/** A roster of stat rows, each with a name, modifier, proficiency dot,
 *  and a roll button. Roll mode toggles whether the tooltip shows
 *  `1d20+N` or `Nd6` notation. */
function StatRollList({
  entries,
  onRoll,
  twoColumn,
  rollMode,
}: {
  entries: Array<{ key: string; label: string; mod: number; proficient: boolean; rollLabel?: string }>
  onRoll: (mod: number, label: string) => void
  twoColumn?: boolean
  rollMode?: RollMode
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: twoColumn ? 'repeat(2, 1fr)' : '1fr',
        gap: '0.25rem 0.625rem',
      }}
    >
      {entries.map((e) => (
        <div
          key={e.key}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.25rem 0.375rem',
            borderRadius: 'var(--ac-radius)',
            background: 'hsl(var(--ac-dark-primary) / 0.4)',
          }}
        >
          <span
            aria-hidden
            title={e.proficient ? 'Proficient' : undefined}
            style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              flexShrink: 0,
              background: e.proficient
                ? 'hsl(var(--ac-mint-primary))'
                : 'hsl(var(--ac-dark-accent))',
            }}
          />
          <span
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: '0.75rem',
              color: 'hsl(var(--ac-text-light))',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {e.label}
          </span>
          <span
            className="font-mono-ac"
            style={{ fontSize: '0.75rem', color: 'hsl(var(--ac-text-muted))' }}
          >
            {formatModifier(e.mod)}
          </span>
          <RollButton
            title={
              rollMode === 'bw'
                ? `Roll ${e.label} — pool ${Math.max(1, e.mod)}d6`
                : `Roll ${e.label} (${d20Notation(e.mod)})`
            }
            onClick={() => onRoll(e.mod, e.rollLabel ?? e.label)}
          />
        </div>
      ))}
    </div>
  )
}

/** Small mint d20 roll button. `corner` absolutely positions it in the
 *  top-right of a `.vtt-stat` cell (used by the Init stat). */
function RollButton({
  title,
  onClick,
  corner,
}: {
  title: string
  onClick: () => void
  corner?: boolean
}) {
  return (
    <IconButton
      type="button"
      size="sm"
      variant="ghost"
      title={title}
      aria-label={title}
      onClick={onClick}
      className={
        '!w-[22px] !h-[22px] flex-shrink-0 !text-[hsl(var(--ac-mint-primary))]' +
        (corner ? ' absolute top-1 right-1' : '')
      }
    >
      <DieIcon size={12} />
    </IconButton>
  )
}

/** Simple d20-ish die glyph. */
function DieIcon({ size = 12 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 2l9 5.25v9.5L12 22l-9-5.25v-9.5L12 2z" />
      <path d="M12 2v20M3 7.25l9 4.75 9-4.75M3 16.75l9-4.75 9 4.75" />
    </svg>
  )
}
